/** Pitch fader, scratching and platter speed — the rest of the physical maths. */

import { clamp, TAU } from '../lib/math.ts';
import { CENTRE, FADER, RPM_33 } from './dims.ts';

/** Fader knob z for a pitch percentage. Positive pitch sits toward the back. */
export const zForPitch = (pct: number): number =>
  FADER.z - (clamp(pct, -FADER.range, FADER.range) / FADER.range) * (FADER.len / 2);

/** Pitch percentage for a world z under the finger. */
export const pitchFromZ = (z: number): number =>
  clamp(((FADER.z - z) / (FADER.len / 2)) * FADER.range, -FADER.range, FADER.range);

/** Playback rate multiplier for a pitch percentage. */
export const rateForPitch = (pct: number): number =>
  1 + clamp(pct, -FADER.range, FADER.range) / 100;

/** Bearing of a world point as seen from the spindle. */
export const discAngle = (x: number, z: number): number => Math.atan2(z - CENTRE.z, x - CENTRE.x);

/** Shortest signed angular difference, wrapped into (-pi, pi]. */
export function angleDelta(to: number, from: number): number {
  let d = to - from;
  while (d > Math.PI) d -= TAU;
  while (d < -Math.PI) d += TAU;
  return d;
}

/** Seconds of audio covered by rotating the record through `radians`. */
export const scratchSeconds = (radians: number, rpm: number = RPM_33): number =>
  (radians / TAU) * (60 / rpm);

/** Platter angular velocity in rad/s. */
export const spinRate = (rpm: number, pitchPct = 0): number =>
  (rpm / 60) * TAU * rateForPitch(pitchPct);
